/* eslint-disable i18next/no-literal-string */
import {Badge} from '@dropbox/dig-components/badge';
import {Button} from '@dropbox/dig-components/buttons';
import {Toggle} from '@dropbox/dig-components/controls';
import {Modal} from '@dropbox/dig-components/modal';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {Select, TextInput} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {snackbarAtom} from 'atoms/snackbar';
import cx from 'classnames';
import {
  type AdminMoveSubtreeResult,
  type AdminTimeframe,
  AdminTimeframesService,
  ApiError,
} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {Column, Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isUserAdmin} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue, useSetAtom} from 'jotai';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import {getService} from 'utilities';
import {productRoadmapQueryKey} from 'views/productRoadmap/hooks';

import {
  AdminTimeframeRoadmapEditor,
  isTimeframeRoadmapValid,
  saveTimeframeRoadmap,
  type TimeframeRoadmapDraft,
} from './AdminTimeframeRoadmapEditor';
import styles from './AdminTimeframes.module.css';

const timeframesQueryKey = ['admin', 'timeframes'] as const;
const TIMEFRAME_TIME_ZONE = 'America/Los_Angeles';
const DAY_MS = 24 * 60 * 60 * 1000;

type FlagName = 'is_visible' | 'show_in_navigation' | 'accepts_new_workstreams';

const columns: Column<AdminTimeframe>[] = [
  {type: 'Name', minWidth: 200},
  {type: 'Starts', fixed: true, minWidth: 120},
  {type: 'Ends', fixed: true, minWidth: 120},
  {type: 'Visible to users', fixed: true, minWidth: 110},
  {type: 'In navigation', fixed: true, minWidth: 110},
  {type: 'Accepts new workstreams', fixed: true, minWidth: 130},
  {type: 'Legacy root', fixed: true, minWidth: 100},
  {type: '', fixed: true, minWidth: 70},
];

const service = () => getService(AdminTimeframesService);

export const errorMessage = (error: unknown) => {
  if (error instanceof ApiError) {
    const detail = (error.body as {detail?: unknown} | undefined)?.detail;
    if (typeof detail === 'string') return detail;
  }
  return 'Something went wrong. Try again.';
};

/** "2026-06-30" -> "Jun 30, 2026", without shifting the day by time zone. */
const formatDate = (value: string) =>
  new Date(`${value}T00:00:00Z`).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

/** Today's date in the timeframes' time zone, as "YYYY-MM-DD". */
const todayInTimeframeZone = (now: Date) =>
  new Intl.DateTimeFormat('en-CA', {timeZone: TIMEFRAME_TIME_ZONE}).format(now);

const dayAfter = (value: string) =>
  new Date(Date.parse(`${value}T00:00:00Z`) + DAY_MS).toISOString().slice(0, 10);

const timeframeBadges = (timeframe: AdminTimeframe, today: string) => {
  const badges: Array<{label: string; variant: 'attention' | 'standard' | 'warning'}> = [];
  if (timeframe.is_current) {
    badges.push({label: 'current', variant: 'attention'});
  } else if (timeframe.start_date > today && timeframe.is_visible) {
    badges.push({label: 'upcoming', variant: 'standard'});
  }
  if (!timeframe.is_visible && timeframe.accepts_new_workstreams) {
    badges.push({label: 'being prepared', variant: 'warning'});
  } else if (!timeframe.is_visible) {
    badges.push({label: 'hidden', variant: 'standard'});
  }
  return badges;
};

const Timeline = ({timeframes, now}: {timeframes: AdminTimeframe[]; now: Date}) => {
  if (!timeframes.length) return null;
  const start = Date.parse(timeframes[0].starts_at);
  const end = Date.parse(timeframes[timeframes.length - 1].ends_at);
  const span = end - start;
  const position = (instant: number) => `${((instant - start) / span) * 100}%`;
  const today = now.getTime();
  const last = timeframes[timeframes.length - 1];
  const boundaries = [
    ...timeframes.map((timeframe) => ({
      key: timeframe.id,
      date: timeframe.start_date,
      instant: Date.parse(timeframe.starts_at),
    })),
    {key: 'end', date: dayAfter(last.end_date), instant: end},
  ];

  return (
    <Stack gap="8">
      <Box display="flex" justifyContent="space-between" alignItems="baseline">
        <Text isBold>Timeline</Text>
        <Text size="small" color="subtle">
          Today: {formatDate(todayInTimeframeZone(now))}
        </Text>
      </Box>
      <Box>
        <div className={styles.timeline} role="list" aria-label="Timeline">
          {timeframes.map((timeframe) => {
            const duration = Date.parse(timeframe.ends_at) - Date.parse(timeframe.starts_at);
            const isPast = Date.parse(timeframe.ends_at) <= today;
            return (
              <div
                key={timeframe.id}
                role="listitem"
                className={cx(styles.block, {
                  [styles.current]: timeframe.is_current,
                  [styles.past]: isPast,
                  [styles.hidden]: !timeframe.is_visible,
                })}
                style={{width: `${(duration / span) * 100}%`}}
              >
                <Text size="small" isBold color={timeframe.is_current ? 'standard' : 'subtle'}>
                  {timeframe.name}
                  {timeframe.is_current ? ' · current' : ''}
                  {timeframe.is_visible ? '' : ' · hidden'}
                </Text>
              </div>
            );
          })}
          {today >= start && today < end ? (
            <div
              className={styles.todayMarker}
              style={{left: position(today)}}
              data-testid="timeline-today"
            />
          ) : null}
        </div>
        <div className={styles.ticks}>
          {boundaries.map(({key, date, instant}) => (
            <span key={key} className={styles.tick} style={{left: position(instant)}}>
              <Text size="xsmall" color="subtle">
                {formatDate(date)}
              </Text>
            </span>
          ))}
        </div>
      </Box>
    </Stack>
  );
};

const AddTimeframeModal = ({
  open,
  lastTimeframe,
  onClose,
}: {
  open: boolean;
  lastTimeframe: AdminTimeframe | undefined;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const fixedStart = lastTimeframe ? dayAfter(lastTimeframe.end_date) : null;

  useEffect(() => {
    if (open) {
      setName('');
      setStartDate('');
      setEndDate('');
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      service().appendTimeframeApiV1AdminTimeframesAppendPost({
        name: name.trim(),
        end_date: endDate,
        start_date: fixedStart ? undefined : startDate,
      }),
    onSuccess: (timeframes) => {
      queryClient.setQueryData(timeframesQueryKey, timeframes);
      onClose();
    },
  });
  const canSubmit = Boolean(name.trim() && endDate && (fixedStart || startDate));

  return (
    <Modal
      open={open}
      withCloseButton="close"
      aria-label="Add next timeframe"
      onRequestClose={() => {
        if (!mutation.isPending) onClose();
      }}
    >
      <Modal.Header>
        <Text size="large" isBold>
          {lastTimeframe ? 'Add next timeframe' : 'Add the first timeframe'}
        </Text>
        {lastTimeframe ? (
          <Text color="subtle">
            The new timeframe starts where <strong>{lastTimeframe.name}</strong> ends.
          </Text>
        ) : null}
      </Modal.Header>
      <Modal.Body>
        <Stack gap="16">
          <TextInput
            aria-label="Name"
            placeholder="Name, for example H2 FY27"
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
          />
          <Box display="flex" style={{gap: 12}}>
            <Stack gap="4" style={{flex: 1}}>
              <Text size="small" isBold>
                Starts
              </Text>
              {fixedStart ? (
                <TextInput aria-label="Starts" value={formatDate(fixedStart)} readOnly disabled />
              ) : (
                <TextInput
                  aria-label="Starts"
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.currentTarget.value)}
                />
              )}
            </Stack>
            <Stack gap="4" style={{flex: 1}}>
              <Text size="small" isBold>
                Last day
              </Text>
              <TextInput
                aria-label="Last day"
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.currentTarget.value)}
              />
            </Stack>
          </Box>
          {lastTimeframe ? (
            <Text size="small" color="subtle">
              The start date is fixed because timeframes can’t overlap or leave gaps. To change it,
              move the end of {lastTimeframe.name}.
            </Text>
          ) : null}
          <Text size="small" color="subtle">
            New timeframes start hidden and closed to new workstreams. Turn on “Accepts new
            workstreams” to import into one while it’s still hidden, and turn on the others when the
            half is ready to announce.
          </Text>
          {mutation.isError ? (
            <Text role="alert" color="error">
              {errorMessage(mutation.error)}
            </Text>
          ) : null}
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="opacity" disabled={mutation.isPending} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isLoading={mutation.isPending}
          disabled={!canSubmit}
          onClick={() => mutation.mutate()}
        >
          Add timeframe
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const moveSubtree = (timeframeId: number, workstreamId: number, dryRun: boolean) =>
  getService(
    AdminTimeframesService
  ).moveSubtreeToTimeframeApiV1AdminTimeframesTimeframeIdMoveSubtreePost(timeframeId, {
    workstream_id: workstreamId,
    dry_run: dryRun,
  });

const PreviewSummary = ({preview}: {preview: AdminMoveSubtreeResult}) => (
  <Stack gap="8">
    <Text>
      <strong>{preview.workstream_name}</strong> and its descendants:{' '}
      {preview.subtree_count === 1 ? '1 workstream' : `${preview.subtree_count} workstreams`},{' '}
      {preview.moving_count} not yet in {preview.timeframe_name}.
    </Text>
    {preview.problems.map((problem) => (
      <Text key={problem} role="alert" color="error">
        {problem}
      </Text>
    ))}
    {preview.outside_parents.length ? (
      <Stack gap="4">
        <Text size="small" isBold>
          Parents outside the subtree
        </Text>
        {preview.outside_parents.map((outside) => (
          <Text key={`${outside.parent_id}-${outside.workstream_id}`} size="small" color="subtle">
            {outside.parent_name} (#{outside.parent_id}) → {outside.workstream_name} (#
            {outside.workstream_id})
          </Text>
        ))}
      </Stack>
    ) : null}
  </Stack>
);

/** Admin tool: move a workstream and everything under it to another timeframe. */
const MoveSubtreeModal = ({
  open,
  timeframes,
  onClose,
}: {
  open: boolean;
  timeframes: AdminTimeframe[];
  onClose: () => void;
}) => {
  const setSnackbar = useSetAtom(snackbarAtom);
  const [workstreamId, setWorkstreamId] = useState('');
  const [timeframeId, setTimeframeId] = useState('');

  const preview = useMutation({
    mutationFn: () => moveSubtree(Number(timeframeId), Number(workstreamId), true),
  });
  const move = useMutation({
    mutationFn: () => moveSubtree(Number(timeframeId), Number(workstreamId), false),
    onSuccess: (result) => {
      setSnackbar({
        text: `Moved ${result.workstream_name} and its descendants to ${result.timeframe_name}.`,
      });
      onClose();
    },
  });
  const {reset: resetPreview} = preview;
  const {reset: resetMove} = move;

  useEffect(() => {
    if (open) {
      setWorkstreamId('');
      setTimeframeId('');
    }
  }, [open]);

  // A preview only applies to the inputs it was made for.
  useEffect(() => {
    resetPreview();
    resetMove();
  }, [workstreamId, timeframeId, resetPreview, resetMove]);

  const inputsReady = /^\d+$/.test(workstreamId.trim()) && Boolean(timeframeId);
  const canMove = Boolean(preview.data && !preview.data.problems.length);
  const isPending = preview.isPending || move.isPending;

  return (
    <Modal
      open={open}
      withCloseButton="close"
      aria-label="Move subtree to timeframe"
      onRequestClose={() => {
        if (!isPending) onClose();
      }}
    >
      <Modal.Header>
        <Text size="large" isBold>
          Move subtree to timeframe
        </Text>
        <Text color="subtle">
          Moves a workstream and all its descendants together, so parents and children stay in the
          same timeframe.
        </Text>
      </Modal.Header>
      <Modal.Body>
        <Stack gap="16">
          <TextInput
            aria-label="Workstream ID"
            placeholder="Workstream ID, for example 2341"
            inputMode="numeric"
            value={workstreamId}
            onChange={(event) => setWorkstreamId(event.currentTarget.value)}
          />
          <Select
            id="move-subtree-timeframe"
            aria-label="Target timeframe"
            placeholder="Target timeframe"
            value={timeframeId}
            onChange={setTimeframeId}
          >
            {timeframes.map((timeframe) => (
              <Select.Option key={timeframe.id} value={String(timeframe.id)}>
                {timeframe.accepts_new_workstreams
                  ? timeframe.name
                  : `${timeframe.name} (closed to new workstreams)`}
              </Select.Option>
            ))}
          </Select>
          {preview.data ? <PreviewSummary preview={preview.data} /> : null}
          {preview.isError || move.isError ? (
            <Text role="alert" color="error">
              {errorMessage(preview.error ?? move.error)}
            </Text>
          ) : null}
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="opacity" disabled={isPending} onClick={onClose}>
          Cancel
        </Button>
        {canMove ? (
          <Button variant="primary" isLoading={move.isPending} onClick={() => move.mutate()}>
            Move {preview.data?.subtree_count === 1 ? 'workstream' : 'workstreams'}
          </Button>
        ) : (
          <Button
            variant="primary"
            isLoading={preview.isPending}
            disabled={!inputsReady}
            onClick={() => preview.mutate()}
          >
            Preview
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const EditTimeframeModal = ({
  timeframe,
  previous,
  next,
  onClose,
}: {
  timeframe: AdminTimeframe | null;
  previous: AdminTimeframe | undefined;
  next: AdminTimeframe | undefined;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [legacyRoot, setLegacyRoot] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [roadmapDraft, setRoadmapDraft] = useState<TimeframeRoadmapDraft>({title: '', url: ''});
  const initializedTimeframeId = useRef<number | null>(null);

  useEffect(() => {
    if (initializedTimeframeId.current === (timeframe?.id ?? null)) return;
    initializedTimeframeId.current = timeframe?.id ?? null;
    if (timeframe) {
      setName(timeframe.name);
      setStartDate(timeframe.start_date);
      setEndDate(timeframe.end_date);
      setLegacyRoot(timeframe.legacy_root_workstream_id?.toString() ?? '');
      setConfirmingDelete(false);
      setRoadmapDraft({
        title: timeframe.product_roadmap?.title ?? '',
        url: timeframe.product_roadmap?.url ?? '',
      });
    }
  }, [timeframe]);

  const save = useMutation({
    mutationFn: async () => {
      if (!timeframe) return;
      const trimmedRoot = legacyRoot.trim();
      const rootId = trimmedRoot ? Number(trimmedRoot) : null;
      let latest: AdminTimeframe[] | undefined;
      if (
        name.trim() !== timeframe.name ||
        rootId !== (timeframe.legacy_root_workstream_id ?? null)
      ) {
        latest = await service().updateTimeframeApiV1AdminTimeframesTimeframeIdPatch(timeframe.id, {
          name: name.trim() !== timeframe.name ? name.trim() : undefined,
          legacy_root_workstream_id: rootId ?? undefined,
          clear_legacy_root: rootId === null && timeframe.legacy_root_workstream_id != null,
        });
      }
      if (startDate !== timeframe.start_date) {
        latest = await service().setTimeframeStartApiV1AdminTimeframesTimeframeIdStartPut(
          timeframe.id,
          {start_date: startDate}
        );
      }
      if (endDate !== timeframe.end_date) {
        latest = await service().setTimeframeEndApiV1AdminTimeframesTimeframeIdEndPut(
          timeframe.id,
          {end_date: endDate}
        );
      }
      const saved = await saveTimeframeRoadmap(
        timeframe,
        roadmapDraft.title.trim() || roadmapDraft.url.trim() ? roadmapDraft : null
      );
      if (saved !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: productRoadmapQueryKey({timeframeId: timeframe.id}),
        });
        latest = (latest ?? queryClient.getQueryData<AdminTimeframe[]>(timeframesQueryKey))?.map(
          (item) => (item.id === timeframe.id ? {...item, product_roadmap: saved} : item)
        );
      }
      return latest;
    },
    onSuccess: (latest) => {
      if (latest) queryClient.setQueryData(timeframesQueryKey, latest);
      onClose();
    },
    onSettled: () => void queryClient.invalidateQueries({queryKey: timeframesQueryKey}),
  });

  const remove = useMutation({
    mutationFn: () => service().deleteTimeframeApiV1AdminTimeframesTimeframeIdDelete(timeframe!.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: timeframesQueryKey});
      onClose();
    },
  });

  const isPending = save.isPending || remove.isPending;
  const error = save.error ?? remove.error;
  const legacyRootIsValid = !legacyRoot.trim() || /^\d+$/.test(legacyRoot.trim());
  const canDelete = Boolean(timeframe) && (!previous || !next);

  return (
    <Modal
      open={Boolean(timeframe)}
      withCloseButton="close"
      aria-label="Edit timeframe"
      onRequestClose={() => {
        if (!isPending) onClose();
      }}
    >
      <Modal.Header>
        <Text size="large" isBold>
          Edit {timeframe?.name}
        </Text>
      </Modal.Header>
      <Modal.Body>
        <Stack gap="16">
          <Stack gap="4">
            <Text size="small" isBold>
              Name
            </Text>
            <TextInput
              aria-label="Name"
              value={name}
              onChange={(event) => setName(event.currentTarget.value)}
            />
          </Stack>
          <Box display="flex" style={{gap: 12}}>
            <Stack gap="4" style={{flex: 1}}>
              <Text size="small" isBold>
                Starts
              </Text>
              <TextInput
                aria-label="Starts"
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.currentTarget.value)}
              />
            </Stack>
            <Stack gap="4" style={{flex: 1}}>
              <Text size="small" isBold>
                Last day
              </Text>
              <TextInput
                aria-label="Last day"
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.currentTarget.value)}
              />
            </Stack>
          </Box>
          {previous || next ? (
            <Text size="small" color="subtle">
              Timeframes can’t overlap or leave gaps.
              {previous ? ` Moving the start also moves the end of ${previous.name}.` : ''}
              {next ? ` Moving the last day also moves the start of ${next.name}.` : ''}
            </Text>
          ) : null}
          <Stack gap="4">
            <Text size="small" isBold>
              Legacy root workstream ID
            </Text>
            <TextInput
              aria-label="Legacy root workstream ID"
              placeholder="None"
              inputMode="numeric"
              value={legacyRoot}
              isInvalid={!legacyRootIsValid}
              onChange={(event) => setLegacyRoot(event.currentTarget.value)}
            />
            <Text size="small" color="subtle">
              The narrative root workstream this timeframe replaces. Leave it empty if there isn’t
              one.
            </Text>
          </Stack>
          <AdminTimeframeRoadmapEditor
            value={roadmapDraft}
            onChange={setRoadmapDraft}
            disabled={isPending}
            hasExistingRoadmap={Boolean(timeframe?.product_roadmap)}
          />
          {error ? (
            <Text role="alert" color="error">
              {errorMessage(error)}
            </Text>
          ) : null}
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        {canDelete ? (
          confirmingDelete ? (
            <Button
              variant="primary"
              tone="destructive"
              isLoading={remove.isPending}
              disabled={isPending}
              onClick={() => remove.mutate()}
            >
              Delete {timeframe?.name}
            </Button>
          ) : (
            <Button
              variant="opacity"
              disabled={isPending}
              onClick={() => setConfirmingDelete(true)}
            >
              Delete
            </Button>
          )
        ) : null}
        <Box style={{flex: 1}} />
        <Button variant="opacity" disabled={isPending} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isLoading={save.isPending}
          disabled={
            isPending ||
            !name.trim() ||
            !startDate ||
            !endDate ||
            !legacyRootIsValid ||
            !isTimeframeRoadmapValid(roadmapDraft)
          }
          onClick={() => save.mutate()}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const TimeframeRow = ({
  timeframe,
  today,
  onEdit,
}: {
  timeframe: AdminTimeframe;
  today: string;
  onEdit: () => void;
}) => {
  const queryClient = useQueryClient();
  const setSnackbar = useSetAtom(snackbarAtom);
  const mutation = useMutation({
    mutationFn: (change: Partial<Record<FlagName, boolean>>) =>
      service().updateTimeframeApiV1AdminTimeframesTimeframeIdPatch(timeframe.id, change),
    onSuccess: (timeframes) => queryClient.setQueryData(timeframesQueryKey, timeframes),
    onError: (error) => setSnackbar({text: errorMessage(error)}),
  });

  const flagToggle = (flag: FlagName, label: string, disabled = false) => (
    <Toggle
      aria-label={`${label} for ${timeframe.name}`}
      checked={timeframe[flag]}
      disabled={disabled || mutation.isPending}
      onChange={() => mutation.mutate({[flag]: !timeframe[flag]})}
    />
  );

  return (
    <Table.Row data-testid={`timeframe-row-${timeframe.id}`}>
      <Table.Cell>
        <div className={styles.badges}>
          <Text isBold>{timeframe.name}</Text>
          {timeframeBadges(timeframe, today).map((badge) => (
            <Badge key={badge.label} variant={badge.variant}>
              {badge.label}
            </Badge>
          ))}
        </div>
      </Table.Cell>
      <Table.Cell>
        <Text>{formatDate(timeframe.start_date)}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text>{formatDate(timeframe.end_date)}</Text>
      </Table.Cell>
      <Table.Cell>{flagToggle('is_visible', 'Visible to users')}</Table.Cell>
      <Table.Cell>
        {flagToggle('show_in_navigation', 'In navigation', !timeframe.is_visible)}
      </Table.Cell>
      <Table.Cell>{flagToggle('accepts_new_workstreams', 'Accepts new workstreams')}</Table.Cell>
      <Table.Cell>
        <Text color="subtle">
          {timeframe.legacy_root_workstream_id ? `#${timeframe.legacy_root_workstream_id}` : '—'}
        </Text>
      </Table.Cell>
      <Table.Cell>
        <Button variant="transparent" size="small" onClick={onEdit}>
          Edit
        </Button>
      </Table.Cell>
    </Table.Row>
  );
};

export const AdminTimeframes = () => {
  const navigate = useNavigate();
  const pulseUser = useAtomValue(pulseUserAtom);
  const canView = isUserAdmin(pulseUser);
  const [addOpen, setAddOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [now] = useState(() => new Date());

  useDocumentTitle('Timeframes');

  const {data, isError, isLoading} = useQuery({
    queryKey: timeframesQueryKey,
    queryFn: () => service().listAdminTimeframesApiV1AdminTimeframesGet(),
    enabled: canView,
  });

  const sorted = useMemo(
    () => [...(data ?? [])].sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at)),
    [data]
  );
  const newestFirst = useMemo(() => [...sorted].reverse(), [sorted]);

  if (!pulseUser) return null;
  if (!canView) {
    navigate('/');
    return null;
  }

  const today = todayInTimeframeZone(now);
  const current = sorted.find((timeframe) => timeframe.is_current);
  const editingIndex = sorted.findIndex((timeframe) => timeframe.id === editingId);
  const editing = editingIndex >= 0 ? sorted[editingIndex] : null;

  let warning: string | null = null;
  if (data && sorted.length && !current) {
    warning =
      'No timeframe covers today. Add the next timeframe or move the last end date so pages have a current timeframe.';
  } else if (current && !(current.is_visible && current.show_in_navigation)) {
    warning = `The current timeframe, ${current.name}, isn't visible and in navigation, so users won't see it in the workstreams picker.`;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Timeframes', to: ROUTE_PATHS.ADMIN_TIMEFRAMES},
      ]}
      width="100%"
    >
      <Stack gap="24">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          style={{gap: 16}}
        >
          <Stack gap="8">
            <Title size={24}>Timeframes</Title>
            <Text color="subtle">
              Planning periods, sorted by start date. Each one starts exactly where the previous one
              ends. Dates are midnight America/Los_Angeles.
            </Text>
          </Stack>
          <Box display="flex" style={{gap: 8}}>
            <Button
              variant="outline"
              onClick={() => navigate(ROUTE_PATHS.ADMIN_TIMEFRAME_ASSIGNMENT)}
            >
              Timeframe assignment
            </Button>
            <Button variant="outline" disabled={!sorted.length} onClick={() => setMoveOpen(true)}>
              Move subtree
            </Button>
            <Button variant="primary" disabled={!data} onClick={() => setAddOpen(true)}>
              {sorted.length ? 'Add next timeframe' : 'Add the first timeframe'}
            </Button>
          </Box>
        </Box>

        {warning ? (
          <SystemBanner tone="warning" role="status">
            <Text>{warning}</Text>
          </SystemBanner>
        ) : null}

        {isError ? (
          <SystemBanner tone="alert" role="alert">
            <Text>Unable to load timeframes. Try refreshing the page.</Text>
          </SystemBanner>
        ) : null}

        <Timeline timeframes={sorted} now={now} />

        <Table columns={columns} data={newestFirst} compact>
          <Table.Header />
          <Table.Body>
            {isLoading ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Box display="flex" alignItems="center" style={{gap: 8}}>
                    <Spinner size="small" />
                    <Text color="subtle">Loading timeframes</Text>
                  </Box>
                </Table.Cell>
              </Table.Row>
            ) : isError ? null : newestFirst.length ? (
              newestFirst.map((timeframe) => (
                <TimeframeRow
                  key={timeframe.id}
                  timeframe={timeframe}
                  today={today}
                  onEdit={() => setEditingId(timeframe.id)}
                />
              ))
            ) : (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Box paddingY="16">
                    <Text color="subtle">No timeframes yet. Add the first one to get started.</Text>
                  </Box>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>

        <Text size="small" color="subtle">
          “In navigation” needs “Visible to users”. A visible timeframe that isn’t in navigation is
          listed under “More timeframes” in the workstreams picker. Only the first or last timeframe
          can be deleted.
        </Text>
      </Stack>

      <AddTimeframeModal
        open={addOpen}
        lastTimeframe={sorted[sorted.length - 1]}
        onClose={() => setAddOpen(false)}
      />
      <MoveSubtreeModal open={moveOpen} timeframes={sorted} onClose={() => setMoveOpen(false)} />
      <EditTimeframeModal
        timeframe={editing}
        previous={editingIndex > 0 ? sorted[editingIndex - 1] : undefined}
        next={editingIndex >= 0 ? sorted[editingIndex + 1] : undefined}
        onClose={() => setEditingId(null)}
      />
    </Layout.Container>
  );
};
